import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './LoanPayment.css';
import { maskAadhaar, maskPAN } from '../../utils/dataMaskingUtils';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';

const LoanPayment = () => {
  const { id, type } = useParams();
  const searchType = type || 'name';
  const [loanDetails, setLoanDetails] = useState(null);
  const [emiDetails, setEmiDetails] = useState([]);
  const [paidDates, setPaidDates] = useState([]);
  const [showOverdueInterest, setShowOverdueInterest] = useState(false);
  const [overdueInterestRate, setOverdueInterestRate] = useState(15); // Default 15% per month (can be empty)
  const [overdueDetails, setOverdueDetails] = useState({ rate: 15, details: [] });
  const [overduePayAmounts, setOverduePayAmounts] = useState({}); // keyed by due_date
  const [selectedDueDate, setSelectedDueDate] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [lastPaymentTransactions, setLastPaymentTransactions] = useState([]);

  useEffect(() => {
    const fetchLoanDetails = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/loan-reciever`, {
          params: { type: searchType, value: id },
        });
        setLoanDetails(response.data);
      } catch (error) {
        console.error('Error fetching loan details:', error);
      }
    };

    fetchLoanDetails();
  }, [id, searchType]);

  useEffect(() => {
    if (loanDetails && loanDetails.account_no) {
      const fetchEmiDetails = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/emi-details/${loanDetails.account_no}`);
          console.log('EMI details:', response.data);
          setEmiDetails(response.data);
        } catch (error) {
          console.error('Error fetching EMI details:', error);
        }
      };

      const fetchPaidDates = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/fetch-paid-status/${loanDetails.account_no}`);
          setPaidDates(response.data);
        } catch (error) {
          console.error('Error fetching paid status:', error);
        }
      };

      const fetchTransactions = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/emi-transactions/${loanDetails.account_no}`);
          console.log('Transactions:', response.data);
          setTransactions(response.data || []);
        } catch (error) {
          console.error('Error fetching transactions:', error);
        }
      };

      fetchEmiDetails();
      fetchPaidDates();
      fetchTransactions();
    }
  }, [loanDetails]);

  // Fetch overdue details from backend (dynamic rate)
  useEffect(() => {
    const fetchOverdueDetails = async () => {
      if (!loanDetails || !loanDetails.account_no) return;
      try {
        // Use default 15 if rate is empty
        const rateToUse = overdueInterestRate === '' || !overdueInterestRate ? 15 : overdueInterestRate;
        const response = await axios.get(`${API_BASE_URL}/overdue-details/${loanDetails.account_no}`, {
          params: { rate: rateToUse }
        });
        console.log('Overdue details:', response.data);
        setOverdueDetails(response.data);
      } catch (error) {
        console.error('Error fetching overdue details:', error);
      }
    };
    fetchOverdueDetails();
  }, [loanDetails, overdueInterestRate]);
  

  // Handle overdue payment submission
  const handleOverduePayment = async (detail) => {
    try {
      const amount = Number(overduePayAmounts[detail.due_date] || 0);
      if (!amount || amount <= 0) {
        alert('Enter a valid overdue payment amount.');
        return;
      }
      await axios.post(`${API_BASE_URL}/pay-overdue`, {
        account_no: loanDetails.account_no,
        due_date: detail.due_date,
        amount,
        payment_mode: 'Overdue',
        transaction_id: `OD-${Date.now()}`,
      });
      alert('Overdue Payment Successful!');
      // Refresh overdue details
      const rateToUse = overdueInterestRate === '' || !overdueInterestRate ? 15 : overdueInterestRate;
      const odRes = await axios.get(`${API_BASE_URL}/overdue-details/${loanDetails.account_no}`, { params: { rate: rateToUse } });
      setOverdueDetails(odRes.data);
    } catch (error) {
      console.error('Error processing overdue payment:', error);
      alert('Overdue Payment failed. Please try again.');
    }
  };

  // Handle payment submission
  const handlePayment = async () => {
    if (!selectedDueDate || !paymentMode) {
      alert('Please select due date and payment mode.');
      return;
    }
    const emiRow = emiDetails.find(e => e.due_date === selectedDueDate);
    if (!emiRow) {
      alert('Selected due date is not available.');
      return;
    }
    const remaining = Number(emiRow.remaining_amount);
    const amount = Number(paymentAmount);
    if (!amount || amount <= 0) {
      alert('Enter a valid payment amount.');
      return;
    }
    // Allow excess payments - excess will be automatically applied to next EMI

    try {
      const response = await axios.post(`${API_BASE_URL}/pay-emi`, {
        account_no: loanDetails.account_no,
        due_date: selectedDueDate,
        payment_mode: paymentMode,
        transaction_id: transactionId.trim() || null,
        amount: Number(amount),
      });
      // Show success message with excess payment info if applicable
      const successMessage = response.data?.message || 'Payment Successful!';
      alert(successMessage);
      setIsPaymentSuccessful(true);
      setLastPaymentTransactions(response.data?.transactions || []);
      setSelectedDueDate('');
      setPaymentMode('');
      setTransactionId('');
      setPaymentAmount('');
      // Refresh EMI details and transactions
      const [emiRes, txRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/emi-details/${loanDetails.account_no}`),
        axios.get(`${API_BASE_URL}/emi-transactions/${loanDetails.account_no}`)
      ]);
      setEmiDetails(emiRes.data);
      setTransactions(txRes.data || []);
    } catch (error) {
      if (error.response && error.response.data.error === 'Paid') {
        alert('Already Paid');
      } else {
        console.error('Error processing payment:', error);
        const apiError = error.response?.data?.error;
        const apiDetails = error.response?.data?.details;
        alert(
          apiDetails
            ? `Payment failed: ${apiError || 'Error'}\n${apiDetails}`
            : apiError || 'Payment failed. Please try again.'
        );
      }
    }
  };

  // Generate and print payment slip (single transaction or aggregate)
  const generateSlip = (opts = {}) => {
    const { payment } = opts; // payment: { amount, payment_date, payment_mode, transaction_id, due_date }
    const amountToShow = payment?.amount ?? 0;
    const slipDate = payment?.payment_date ? new Date(payment.payment_date) : new Date();
    const modeToShow = payment?.payment_mode || paymentMode || '';
    const txToShow = payment?.transaction_id || transactionId || '';

    // Calculate total remaining balance (sum of all remaining amounts from all EMIs)
    const totalRemainingBalance = emiDetails.reduce((sum, emi) => {
      const remaining = Number(emi.remaining_amount || 0);
      // If remaining is <= 5, treat as 0 (already paid)
      return sum + (remaining <= 5 ? 0 : remaining);
    }, 0);

    const slipContent = `
  
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  .container { display: flex; }
  .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 15px; }
  .details { margin: 10px 0; }
  .line { display: flex; justify-content: space-between; margin: 5px 0; }
  .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  .table td { padding: 5px; }
  .table .right { text-align: right; font-weight: bold; }
  .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
  .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
  .balance { font-weight: bold; font-size: 1.3em; margin-top: 10px; text-align: center; }
</style>

<div class="container">
  <!-- Left Receipt -->
  <div class="left-receipt">
    <div class="header">SHREE DHANESHWARI FINANCE AND LEASING</div>
    <div class="sub-header">Basveshwar Chowk,Main road, - Kasgi , Osmanabad</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(slipDate)}</div>
    </div>
    <div class="details">
    <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
    <div class="line"><strong>Amount Rs:</strong> ₹${Number(amountToShow).toFixed(2)}</div>
   </div>
    <table class="table">
        <tr>
          <td>EMI PAYMENT</td>
          <td class="right">${Number(amountToShow).toFixed(2)}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹${Number(amountToShow).toFixed(2)}
      </div>
      <div class="balance">
        Bal: ₹${totalRemainingBalance.toFixed(2)}
      </div>
    <div class="footer">
      Cashier / Mg. Partner                      
    </div>
  </div>

  <!-- Right Receipt -->
  <div class="right-receipt">
    <div class="header">SHREE DHANESHWARI FINANCE AND LEASING</div>
    <div class="sub-header">Kasgi , Osmanabad</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(slipDate)}</div>
    </div>
   <div class="details">
     
      <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
      <div class="line"><strong>Amount Rs:</strong> ₹${Number(amountToShow).toFixed(2)}</div>
    </div>
    <table class="table">
      <tr>
          <td>EMI PAYMENT</td>
          <td class="right">${Number(amountToShow).toFixed(2)}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹${Number(amountToShow).toFixed(2)}
      </div>
      <div class="balance">
        Bal: ₹${totalRemainingBalance.toFixed(2)}
      </div>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>
</div>
`;
  const printWindow = window.open('', '', 'height=800,width=1200');
  printWindow.document.write('<html><head><title>Interest Slip</title></head><body>');
  printWindow.document.write(slipContent);
  printWindow.document.write('</body></html>');
  printWindow.document.close();
  printWindow.print();
};

  const viewSlipForDueDate = (dueDate) => {
    const txs = transactions.filter(t => t.due_date === dueDate);
    if (txs.length === 0) {
      alert('No transactions found for this EMI.');
      return;
    }
    // For now print individual slips for each payment in that EMI
    txs.forEach(tx => generateSlip({ payment: tx }));
  };

  const openAllSlips = () => setShowSlipModal(true);
  const closeAllSlips = () => setShowSlipModal(false);

  // Toggle overdue interest table on Ctrl + T key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'b') {
        setShowOverdueInterest((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!loanDetails) {
    return <h2>Loading loan details...</h2>;
  }

  return (
    <div className="loan-payment-container">
      <div className="loan-payment-header">Loan Payment</div>

      {/* Loan Details */}
      <div className="loan-details">
        <p><strong>Account No:</strong> {loanDetails.account_no}</p>
        <p><strong>Loanee Name:</strong> {loanDetails.loanee_name}</p>
        <p><strong>Date:</strong> {formatDateToDDMMYYYY(loanDetails.date)}</p>
        <p><strong>Aadhaar Debtor:</strong> {maskAadhaar(loanDetails.aadhaar_debtor)}</p>
        <p><strong>PAN Card:</strong> {maskPAN(loanDetails.pan_card)}</p>
        <p><strong>Address Debtor:</strong> {loanDetails.address_debtor}</p>
        <p><strong>Guarantor Name:</strong> {loanDetails.guarantor_name}</p>
        <p><strong>Loan Amount:</strong> ₹{loanDetails.loan_amount}</p>
        <p><strong>Period:</strong> {loanDetails.period} days</p>
      </div>

      {/* EMI Details */}
      <div className="emi-details">
        <h3>EMI Details</h3>
        <table>
          <thead>
            <tr>
              <th>Due Date</th>
              <th>EMI Amount</th>
              <th>Paid</th>
              <th>Remaining</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {emiDetails.map((emi, index) => (
              <tr key={index}>
                <td>{formatDateToDDMMYYYY(emi.due_date)}</td>
                <td>₹{parseFloat(emi.monthly_installment).toFixed(2)}</td>
                <td>₹{Number(emi.paid_amount || 0).toFixed(2)}</td>
                <td>₹{Number(emi.remaining_amount || 0).toFixed(2)}</td>
                <td>
                  <span className={`status-badge ${emi.status === 'Paid' ? 'status-paid' : emi.status === 'Partially Paid' ? 'status-partial' : 'status-unpaid'}`}>
                    {emi.status}
                  </span>
                </td>
                <td>
                  {Number(emi.paid_amount || 0) > 0 && (
                    <button onClick={() => viewSlipForDueDate(emi.due_date)}>View Slip</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Overdue Interest Details */}
      {showOverdueInterest && (
        <div className="overdue-interest-details" style={{ 
          marginTop: '20px', 
          padding: '20px', 
          border: '2px solid #6c757d', 
          borderRadius: '8px', 
          backgroundColor: '#f8f9fa' 
        }}>
          <h3 style={{ marginTop: 0, color: '#6c757d', marginBottom: '20px' }}>Overdue Interest Details</h3>
          
          <div style={{ marginBottom: '20px', padding: '15px', background: 'white', borderRadius: '5px' }}>
            <label style={{ marginRight: '10px', fontWeight: 'bold', color: '#4d3221' }}>Overdue Interest Rate (% per month):</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={overdueInterestRate === '' ? '' : overdueInterestRate}
              onChange={(e) => {
                const inputValue = e.target.value;
                const rate = inputValue === '' ? '' : parseFloat(inputValue);
                setOverdueInterestRate(rate === '' || isNaN(rate) ? '' : rate);
              }}
              style={{ 
                padding: '8px', 
                width: '120px', 
                borderRadius: '4px',
                border: '1px solid #d3b899',
                fontSize: '16px'
              }}
            />
            <small style={{ color: '#666', display: 'block', marginTop: '8px' }}>
              Default: 15% per month. Modify as needed. Press Ctrl+B to hide this section.
            </small>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse', 
              background: 'white', 
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <thead>
                <tr style={{ background: '#d8c3a5' }}>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', color: '#3b2c20', fontWeight: 'bold' }}>Due Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', color: '#3b2c20', fontWeight: 'bold' }}>Remaining Amount</th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', color: '#3b2c20', fontWeight: 'bold' }}>Days Due</th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', color: '#3b2c20', fontWeight: 'bold' }}>Total Overdue Interest</th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', color: '#3b2c20', fontWeight: 'bold' }}>Overdue Paid</th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', color: '#3b2c20', fontWeight: 'bold' }}>Remaining Overdue</th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', color: '#3b2c20', fontWeight: 'bold' }}>Payment Amount</th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', color: '#3b2c20', fontWeight: 'bold' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(overdueDetails.details || []).map((detail, index) => (
                  <tr key={index} style={{ background: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{formatDateToDDMMYYYY(detail.due_date)}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>₹{Number(detail.remaining_amount).toFixed(2)}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{detail.days_due}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>₹{Number(detail.total_overdue_interest).toFixed(2)}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>₹{Number(detail.overdue_paid).toFixed(2)}</td>
                    <td style={{ padding: '10px', border: '1px solid #ddd', fontWeight: detail.remaining_overdue > 0 ? 'bold' : 'normal', color: detail.remaining_overdue > 0 ? '#c12c2c' : '#333' }}>
                      ₹{Number(detail.remaining_overdue).toFixed(2)}
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                      <input
                        type="number"
                        value={overduePayAmounts[detail.due_date] || ''}
                        onChange={(e) => setOverduePayAmounts(prev => ({ ...prev, [detail.due_date]: e.target.value }))}
                        placeholder="Amount"
                        style={{ 
                          padding: '6px', 
                          width: '100px', 
                          borderRadius: '4px',
                          border: '1px solid #d3b899',
                          fontSize: '14px'
                        }}
                      />
                    </td>
                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                      <button 
                        onClick={() => handleOverduePayment(detail)}
                        style={{
                          padding: '6px 12px',
                          background: '#8b5e3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '500',
                          fontSize: '14px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#6a4631'}
                        onMouseOut={(e) => e.target.style.background = '#8b5e3c'}
                      >
                        Pay Overdue
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {overdueDetails.details && overdueDetails.details.length === 0 && (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#666', 
              background: 'white', 
              borderRadius: '5px',
              marginTop: '15px'
            }}>
              No overdue interest records found.
            </div>
          )}
        </div>
      )}

      {/* Payment Form */}
      <div className="payment-form">
        <h3>Make Payment</h3>
        <div>
          <label>Due Date:</label>
          <select value={selectedDueDate} onChange={(e) => setSelectedDueDate(e.target.value)}>
            <option value="">Select Due Date</option>
            {emiDetails.map((emi, index) => (
              <option key={index} value={emi.due_date}>
                {formatDateToDDMMYYYY(emi.due_date)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Payment Amount:</label>
          <input
            type="number"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            placeholder="Enter amount"
          />
          {selectedDueDate && (() => {
            const emiRow = emiDetails.find(e => e.due_date === selectedDueDate);
            if (!emiRow) return null;
            const remaining = Number(emiRow.remaining_amount) || 0;
            const amount = Number(paymentAmount) || 0;
            const excess = amount > remaining ? amount - remaining : 0;
            return (
              <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                <div>Remaining for this EMI: ₹{remaining.toFixed(2)}</div>
                {excess > 0 && (
                  <div style={{ color: '#28a745', fontWeight: 'bold', marginTop: '3px' }}>
                    Excess ₹{excess.toFixed(2)} will be applied to next EMI
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <div>
  <label>Payment Mode:</label>
  <select
    value={paymentMode}
    onChange={(e) => setPaymentMode(e.target.value)}
  >
    <option value="">Select</option>
    <option value="Cash">Cash</option>
    <option value="UPI">UPI</option>
    <option value="Bank Transfer">Bank Transfer</option>
    <option value="Cheque">Cheque</option>
  </select>
</div>
        <div>
          <label>Transaction ID:</label>
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            placeholder="Enter Transaction ID"
          />
        </div>
        <button onClick={handlePayment}>Submit Payment</button>
      </div>

      {/* Slip Button */}
      {isPaymentSuccessful && lastPaymentTransactions.length > 0 && (
        <button
          onClick={() => lastPaymentTransactions.forEach((tx) => generateSlip({ payment: tx }))}
          className="slip-button"
        >
          Generate Slip{lastPaymentTransactions.length > 1 ? 's' : ''}
        </button>
      )}

      {/* Transaction History */}
      <div className="emi-details" style={{ marginTop: '20px' }}>
        <h3>Transaction History</h3>
        <table>
          <thead>
            <tr>
              <th>Payment Date</th>
              <th>Amount</th>
              <th>Mode</th>
              <th>Transaction ID</th>
              <th>Due Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length > 0 ? (
              [...transactions]
                .sort((a, b) => {
                  const ad = a.payment_date || '';
                  const bd = b.payment_date || '';
                  return bd.localeCompare(ad);
                })
                .map((tx) => (
                  <tr key={tx.id || `${tx.due_date}-${tx.payment_date}-${tx.amount}-${tx.transaction_id || ''}`}>
                    <td>{tx.payment_date ? formatDateToDDMMYYYY(tx.payment_date) : '-'}</td>
                    <td>₹{Number(tx.amount).toFixed(2)}</td>
                    <td>{tx.payment_mode || 'N/A'}</td>
                    <td>{tx.transaction_id || 'N/A'}</td>
                    <td>{tx.due_date ? formatDateToDDMMYYYY(tx.due_date) : '-'}</td>
                    <td>
                      <button onClick={() => generateSlip({ payment: tx })}>Print Slip</button>
                    </td>
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center' }}>No transactions found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* View All Slips Modal Trigger */}
      <div style={{ marginTop: 10 }}>
        <button onClick={openAllSlips}>View All Payment Slips</button>
      </div>

      {showSlipModal && (
        <div className="modal-overlay" onClick={closeAllSlips}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payment Slips</h3>
              <button className="modal-close" onClick={closeAllSlips}>×</button>
            </div>
            <div className="modal-body">
              {(transactions || []).map((tx) => (
                <div key={tx.id || `${tx.due_date}-${tx.payment_date}-${tx.amount}-${tx.transaction_id || ''}`} className="slip-row">
                  <div><strong>Due:</strong> {formatDateToDDMMYYYY(tx.due_date)}</div>
                  <div><strong>Date:</strong> {tx.payment_date ? formatDateToDDMMYYYY(tx.payment_date) : '-'}</div>
                  <div><strong>Mode:</strong> {tx.payment_mode}</div>
                  <div><strong>Txn:</strong> {tx.transaction_id}</div>
                  <div><strong>Amount:</strong> ₹{Number(tx.amount).toFixed(2)}</div>
                  <div>
                    <button onClick={() => generateSlip({ payment: tx })}>Print</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanPayment;
